import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  ChevronRightOutlined as OpenIcon,
  LocalShippingOutlined as TruckIcon,
  ScheduleOutlined as TimeIcon,
  TourOutlined as TripsIcon,
} from '@mui/icons-material';
import { endOfMonth, endOfWeek, format, isSameDay, startOfMonth, startOfWeek } from 'date-fns';
import PageHeader from '../../components/PageHeader';
import { APP_SHAPE } from '../../constants/shape';
import { listCollectionPoints } from '../../services/collection-point.service';
import { listRouteRunsForRange } from '../../services/route-run.service';
import { listZones } from '../../services/zone.service';
import { useOrganizationStore } from '../../stores/organization.store';
import type { OpsRouteRun } from '../../types/route-run.type';
import type { Zone } from '../../types/zone.type';
import { formatWhen } from '../../utils/formatters';
import { STOP_STATUS_COLOR } from './tripHelpers';

function RunCard({ run }: { run: OpsRouteRun }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.resolvedLanguage || i18n.language;
  return (
    <Paper
      elevation={1}
      onClick={() => navigate(`/monitoring/trips/${run.id}`)}
      sx={{ borderRadius: `${APP_SHAPE.radiusLg}px`, p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
    >
      <TruckIcon color="primary" />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle1" fontWeight={600} noWrap>
          {run.truck_name ?? run.truck_id} · {run.route_name ?? '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {run.driver_name ?? t('trips.noDriver')} · <TimeIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />{' '}
          {formatWhen(run.clock_in_at, locale, 'time')} → {formatWhen(run.clock_out_at, locale, 'time')}
          {run.offload_at ? ` · ${t('trips.offloaded')} ${formatWhen(run.offload_at, locale, 'time')}` : ''}
          {run.route_snapshot?.city ? ` · ${run.route_snapshot.city}` : ''}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={t(`trips.runStatus.${run.status}`, run.status)} />
        {Object.entries(run.stop_status_counts).map(([status, count]) => (
          <Chip key={status} size="small" color={STOP_STATUS_COLOR[status] ?? 'default'} variant="outlined" label={`${t(`trips.stopStatus.${status}`, status)}: ${count}`} />
        ))}
      </Stack>
      <OpenIcon color="action" />
    </Paper>
  );
}

type RangeMode = 'day' | 'week' | 'month';

// Weeks run Sunday to Saturday, matching the Saudi work week (Sun-Thu).
const WEEK_STARTS_ON = 0;

function rangeFor(anchor: Date, mode: RangeMode): { from: Date; to: Date } {
  if (mode === 'week') return { from: startOfWeek(anchor, { weekStartsOn: WEEK_STARTS_ON }), to: endOfWeek(anchor, { weekStartsOn: WEEK_STARTS_ON }) };
  if (mode === 'month') return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
  return { from: anchor, to: anchor };
}

export default function Trips() {
  const { t, i18n } = useTranslation();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const [runDate, setRunDate] = useState<Date>(new Date());
  const [mode, setMode] = useState<RangeMode>('day');
  const locale = i18n.resolvedLanguage || i18n.language;
  const [runs, setRuns] = useState<OpsRouteRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zones, setZones] = useState<Zone[]>([]);
  const [labelsByPoint, setLabelsByPoint] = useState<Map<string, string[]>>(new Map());

  const [truck, setTruck] = useState<string | null>(null);
  const [driver, setDriver] = useState<string | null>(null);
  const [zone, setZone] = useState<Zone | null>(null);
  const [city, setCity] = useState<string | null>(null);
  const [supplier, setSupplier] = useState<string | null>(null);

  const orgId = selectedOrganization?.id;

  const range = useMemo(() => rangeFor(runDate, mode), [runDate, mode]);

  const load = useCallback(() => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    listRouteRunsForRange(orgId, format(range.from, 'yyyy-MM-dd'), format(range.to, 'yyyy-MM-dd'))
      .then((page) => setRuns(page.runs))
      .catch(() => setError(t('trips.loadError')))
      .finally(() => setLoading(false));
  }, [orgId, range, t]);

  useEffect(() => { load(); }, [load]);

  // Labels live on collection points, not in run snapshots -- one fetch maps them.
  useEffect(() => {
    if (!orgId) return;
    void listZones({ organization_id: orgId, limit: 100 }).then((result) => setZones(result.zones)).catch(() => setZones([]));
    void listCollectionPoints({ organization_id: orgId, limit: 500 })
      .then((result) => setLabelsByPoint(new Map(result.collection_points.map((point) => [point.id, (point.labels ?? []).map((label) => label.id)]))))
      .catch(() => setLabelsByPoint(new Map()));
  }, [orgId]);

  const options = useMemo(() => {
    const unique = (values: (string | null | undefined)[]) => Array.from(new Set(values.filter((v): v is string => Boolean(v)))).sort();
    return {
      trucks: unique(runs.map((run) => run.truck_name)),
      drivers: unique(runs.map((run) => run.driver_name)),
      cities: unique(runs.map((run) => run.route_snapshot?.city)),
      suppliers: unique(runs.flatMap((run) => run.stops.map((stop) => stop.point_snapshot?.supplier_name))),
    };
  }, [runs]);

  const visible = useMemo(
    () =>
      runs.filter((run) => {
        if (truck && run.truck_name !== truck) return false;
        if (driver && run.driver_name !== driver) return false;
        if (city && run.route_snapshot?.city !== city) return false;
        if (supplier && !run.stops.some((stop) => stop.point_snapshot?.supplier_name === supplier)) return false;
        if (zone && !run.stops.some((stop) => (labelsByPoint.get(stop.collection_point_id) ?? []).includes(zone.id))) return false;
        return true;
      }),
    [runs, truck, driver, city, supplier, zone, labelsByPoint],
  );

  const groupedByDay = useMemo(() => {
    const groups: { day: string; runs: OpsRouteRun[] }[] = [];
    for (const run of visible) {
      const last = groups[groups.length - 1];
      if (last && last.day === run.run_date) last.runs.push(run);
      else groups.push({ day: run.run_date, runs: [run] });
    }
    return groups;
  }, [visible]);

  const rangeCaption = isSameDay(range.from, range.to)
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'full' }).format(range.from)
    : `${new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(range.from)} – ${new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(range.to)}`;

  if (!selectedOrganization) return <Alert severity="info">{t('transportation.routes.selectOrganization')}</Alert>;

  return (
    <Box>
      <PageHeader icon={<TripsIcon />} title={t('trips.pageTitle')} subtitle={t('trips.subtitle')} sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }} />

      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: `${APP_SHAPE.radiusLg}px` }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
          <DatePicker
            label={t('trips.dateLabel')}
            value={runDate}
            onChange={(value) => value && setRunDate(value)}
            slotProps={{ textField: { size: 'small', sx: { minWidth: 170 } } }}
          />
          <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, next) => next && setMode(next)}>
            <ToggleButton value="day">{t('trips.range.day')}</ToggleButton>
            <ToggleButton value="week">{t('trips.range.week')}</ToggleButton>
            <ToggleButton value="month">{t('trips.range.month')}</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {rangeCaption} · {t('trips.runCount', { count: visible.length })}
          </Typography>
          <Autocomplete size="small" sx={{ minWidth: 180 }} options={options.trucks} value={truck} onChange={(_, v) => setTruck(v)} renderInput={(params) => <TextField {...params} label={t('trips.filters.truck')} />} />
          <Autocomplete size="small" sx={{ minWidth: 180 }} options={options.drivers} value={driver} onChange={(_, v) => setDriver(v)} renderInput={(params) => <TextField {...params} label={t('trips.filters.driver')} />} />
          <Autocomplete size="small" sx={{ minWidth: 180 }} options={zones} getOptionLabel={(option) => option.name} isOptionEqualToValue={(a, b) => a.id === b.id} value={zone} onChange={(_, v) => setZone(v)} renderInput={(params) => <TextField {...params} label={t('trips.filters.label')} />} />
          <Autocomplete size="small" sx={{ minWidth: 160 }} options={options.cities} value={city} onChange={(_, v) => setCity(v)} renderInput={(params) => <TextField {...params} label={t('trips.filters.city')} />} />
          <Autocomplete size="small" sx={{ minWidth: 200 }} options={options.suppliers} value={supplier} onChange={(_, v) => setSupplier(v)} renderInput={(params) => <TextField {...params} label={t('trips.filters.supplier')} />} />
        </Stack>
      </Paper>

      {error ? <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={load}>{t('suppliers.details.retry')}</Button>}>{error}</Alert> : null}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : visible.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: `${APP_SHAPE.radiusLg}px` }}>
          <Typography variant="body1" color="text.secondary">{t('trips.empty')}</Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {groupedByDay.map((group) => (
            <Box key={group.day}>
              {mode !== 'day' ? (
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  {formatWhen(group.day, locale, 'date')} · {t('trips.runCount', { count: group.runs.length })}
                </Typography>
              ) : null}
              <Stack spacing={1.5}>
                {group.runs.map((run) => <RunCard key={run.id} run={run} />)}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
